import { api } from './client'

/**
 * F-059 / TASK-0313 — conto corrente dei soci (REQ-128-001, REQ-130-001, REQ-170-001, REQ-171-001,
 * REQ-172-001). Convenzione: saldo POSITIVO = la società deve al socio; NEGATIVO = il socio deve alla
 * società. Il saldo è sempre la somma dei movimenti calcolata dal server, mai un campo.
 */

export interface Partner {
  id: number
  firstName: string
  lastName: string
  userId?: number | null
  participationPercentage?: number | null
  distributionPercentage?: number | null
  role?: string | null
  status: string
  /** F-069: sezioni dell'amministrazione abilitate al socio, separate da virgola (vuoto = nessuna). */
  disclosedSections?: string
}

/** F-069 / REQ-206-008: le sezioni che l'Admin puo' abilitare a un socio (lista chiusa lato server). */
export const PARTNER_SECTIONS: { id: string; label: string }[] = [
  { id: 'costi', label: 'Costi (totali mese e anno)' },
  { id: 'budget', label: 'Budget (scostamento dell\'anno)' },
  { id: 'scadenziario', label: 'Scadenziario (uscite a 30 giorni)' },
]

/** F-069: la posizione del socio collegato + SOLO le sezioni abilitate (le altre chiavi sono assenti). */
export interface PartnerOverview {
  partner: { id: number; firstName: string; lastName: string }
  balance: number
  summary: {
    balance: number
    totalCredits: number
    totalDebits: number
    attributedAmounts: number
    liquidatedAmounts: number
    pendingApprovalCount: number
  }
  disclosedSections: string[]
  costs?: {
    month: { from: string; to: string; total: number }
    year: { from: string; to: string; total: number }
  }
  budget?: { year: number; budget: number; actual: number; variance: number; variancePct: number | null } | null
  outflows?: { days: number; from: string; to: string; count: number; total: number }
}

export interface PartnerMe {
  id: number
  firstName: string
  lastName: string
}

export interface PartnerCategoryInfo {
  category: string
  label: string
  /** +1 / -1 se la categoria ha un effetto fisso, null se lo sceglie l'utente. */
  fixedEffect: number | null
}

export interface PartnerCategoryTotal {
  category: string
  label: string
  total: number
  count: number
}

export interface PartnerLedgerMovement {
  id: number
  movementDate: string
  category: string
  categoryLabel: string
  amount: number
  effect: number
  signedAmount: number
  runningBalance: number
  description: string
  documentReference?: string | null
  sourceAccountId?: number | null
  destinationAccountId?: number | null
  costCentre?: string | null
  expenseId?: number | null
  linkedMovementId?: number | null
  reversesMovementId?: number | null
  settledByMovementId?: number | null
  reversedByMovementId?: number | null
  createdByUserId: number
  createdAt: string
  approvedByUserId?: number | null
  approvedAt?: string | null
  notes?: string | null
}

export interface PartnerLedger {
  partner: {
    id: number
    firstName: string
    lastName: string
    userId?: number | null
    participationPercentage?: number | null
    distributionPercentage?: number | null
  }
  balance: number
  summary: {
    balance: number
    totalCredits: number
    totalDebits: number
    attributedAmounts: number
    liquidatedAmounts: number
    pendingApprovalCount: number
    byCategory: PartnerCategoryTotal[]
  }
  movements: PartnerLedgerMovement[]
  categories: PartnerCategoryInfo[]
}

export interface PartnerMovementBody {
  movementDate: string
  category: string
  amount: number
  effect?: number | undefined
  description: string
  documentReference?: string | undefined
  sourceAccountId?: number | undefined
  destinationAccountId?: number | undefined
  costCentre?: string | undefined
  notes?: string | undefined
}

export const partnersApi = {
  /** `GET /api/partners` è `Authenticated` (lettura anagrafica aperta a chiunque: già così prima di F-059). */
  list: () => api<Partner[]>('/api/partners'),

  /** Il socio collegato all'utente corrente; 404 (→ `null`) se non ce n'è. */
  me: async (): Promise<PartnerMe | null> => {
    try {
      return await api<PartnerMe>('/api/partners/me')
    } catch {
      return null
    }
  },

  ledger: (id: number, from?: string, to?: string) => {
    const q = new URLSearchParams()
    if (from) q.set('from', from)
    if (to) q.set('to', to)
    const qs = q.toString()
    return api<PartnerLedger>(`/api/partners/${id}/ledger${qs ? `?${qs}` : ''}`)
  },

  createMovement: (id: number, body: PartnerMovementBody) =>
    api<{ id: number }>(`/api/partners/${id}/movements`, { method: 'POST', body: JSON.stringify(body) }),

  approve: (id: number, movementId: number) =>
    api<void>(`/api/partners/${id}/movements/${movementId}/approve`, { method: 'POST' }),

  reverse: (id: number, movementId: number, reason?: string) =>
    api<{ id: number }>(`/api/partners/${id}/movements/${movementId}/reverse`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    }),

  /** REQ-171-001: spesa pagata personalmente dal socio. */
  expensePaid: (id: number, body: { expenseId: number; sourceAccountId?: number | undefined }) =>
    api<{ id: number }>(`/api/partners/${id}/expense-paid`, { method: 'POST', body: JSON.stringify(body) }),

  /** Compensazione: rimborso della spesa anticipata (REQ-171-001) o versamento dell'incasso per conto azienda (REQ-172-001). */
  settle: (id: number, movementId: number, body: { date: string; accountId: number }) =>
    api<{ id: number }>(`/api/partners/${id}/movements/${movementId}/settle`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** F-069: panoramica del socio collegato al chiamante; 404 (-> `null`) se non e' collegato. */
  overview: async (): Promise<PartnerOverview | null> => {
    try {
      return await api<PartnerOverview>('/api/partners/me/overview')
    } catch {
      return null
    }
  },

  /** F-069 (Admin): sostituisce le sezioni abilitate al socio. */
  setDisclosure: (id: number, sections: string[]) =>
    api<{ sections: string[] }>(`/api/partners/${id}/disclosure`, { method: 'PATCH', body: JSON.stringify({ sections }) }),

  /** Admin: collega (o scollega con `null`) l'utente CRM che è questo socio. */
  linkUser: (id: number, userId: number | null) =>
    api<void>(`/api/partners/${id}/user`, { method: 'PATCH', body: JSON.stringify({ userId }) }),
}
